"use client"

import { useCallback, useEffect, useRef, useState } from 'react'
import { subscribeToConversationChanges } from '@gorilla-works/messages'

import { useAuth } from '@/contexts/auth-context'
import { env } from '@/lib/env'
import { onTabRefocus } from '@/lib/on-tab-refocus'
import type { StageMessageCount } from '@/hooks/use-stage-message-counts'

// Shape returned by GET /conversations/my-states (MessagesService) — counters only.
interface ConversationState {
  entityId: string
  messageCount: number
  unreadCount: number
  hasImportantUnread: boolean
  isSubscribed: boolean
}

// The request-family message entity types whose unread we surface on the ATS / VMS / Client-Requests tabs.
// (Onboarding threads are ClientRequest-family — `Request`/`OnboardingRequest` — so they roll into the CR
// tab; the caller does that grouping.) These are the same reads the inbox does, but persistent so the tab
// badges stay live rather than only while the inbox is open.
const REQUEST_ENTITY_TYPES = ['AtsPlacementRequest', 'VmsRequest', 'ClientRequest', 'Request', 'OnboardingRequest'] as const

const REFRESH_INTERVAL_MS = 5 * 60 * 1000
// After a read/unread/mute the write races the refetch; re-read a few times, staggered, until my-states
// settles — the installed 1.0.14 change signal carries no settled count (mirrors use-stage-message-counts).
const RECONCILE_DELAYS_MS = [1500, 8000, 20000]

/**
 * Per-entity-type unread MESSAGE counts for the request families, keyed by entity type
 * (`AtsPlacementRequest`, `VmsRequest`, `ClientRequest`, `Request`, `OnboardingRequest`). One bounded
 * `my-states` read per type (counters only, no search — each family is a single tab, so there is no
 * per-stage bucketing to resolve). The caller groups these onto the ATS / VMS / Client-Requests tabs.
 *
 * Same gate and cadence as {@link useStageMessageCounts} (5-minute interval + genuine tab-return), plus a
 * live `subscribeToConversationChanges` reconcile so a read/mute inside a request thread updates its badge.
 * Skipped entirely (no calls) when disabled.
 */
export function useRequestMessageCounts(enabled: boolean): Record<string, StageMessageCount> {
  const { token } = useAuth()
  const [counts, setCounts] = useState<Record<string, StageMessageCount>>({})

  const tokenRef = useRef(token)
  tokenRef.current = token
  const requestIdRef = useRef(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconcileTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const refreshRef = useRef<() => void>(() => {})

  const refresh = useCallback(async () => {
    if (!enabled) {
      ++requestIdRef.current // invalidate any in-flight response so it can't repaint after the gate closed
      setCounts({})
      return
    }
    const authToken = tokenRef.current
    if (!authToken) return

    const currentRequestId = ++requestIdRef.current
    try {
      const perType = await Promise.all(
        REQUEST_ENTITY_TYPES.map(async (entityTypeId) => {
          try {
            const res = await fetch(
              `${env.messagesApiUrl}/conversations/my-states?entityTypeId=${encodeURIComponent(entityTypeId)}`,
              { headers: { Authorization: `Bearer ${authToken}` } }
            )
            if (!res.ok) return { entityTypeId, states: [] as ConversationState[] }
            return { entityTypeId, states: (await res.json()) as ConversationState[] }
          } catch {
            return { entityTypeId, states: [] as ConversationState[] }
          }
        })
      )
      if (currentRequestId !== requestIdRef.current) return

      const next: Record<string, StageMessageCount> = {}
      for (const { entityTypeId, states } of perType) {
        let unread = 0
        let hasImportant = false
        for (const s of states) {
          if (!(s.isSubscribed && s.unreadCount > 0)) continue
          unread += s.unreadCount
          hasImportant = hasImportant || s.hasImportantUnread
        }
        if (unread > 0) next[entityTypeId] = { unread, hasImportant }
      }
      setCounts(next)
    } catch {
      // Best-effort — a tab message badge isn't worth surfacing an error for.
    }
  }, [enabled])

  refreshRef.current = refresh

  // Refresh when the gate flips or the token first resolves.
  useEffect(() => {
    void refreshRef.current()
  }, [enabled, token])

  // 5-minute poll + fresh-on-genuine-tab-return (the same onTabRefocus guard the stage counts use).
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => void refreshRef.current(), REFRESH_INTERVAL_MS)
    const stopRefocus = onTabRefocus(() => void refreshRef.current())
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      stopRefocus()
    }
  }, [])

  // Live updates: a read/unread/mute on a request thread fires the package's change signal. Re-read,
  // staggered, because the installed 1.0.14 signal carries no settled count and my-states lags the write.
  useEffect(() => {
    if (!enabled) return
    const scheduleReconcile = () => {
      for (const t of reconcileTimersRef.current) clearTimeout(t)
      reconcileTimersRef.current = RECONCILE_DELAYS_MS.map((ms) => setTimeout(() => void refreshRef.current(), ms))
    }
    const onConversationChange = (entityTypeId: string) => {
      if ((REQUEST_ENTITY_TYPES as readonly string[]).includes(entityTypeId)) scheduleReconcile()
    }
    const unsubscribe = subscribeToConversationChanges(
      onConversationChange as Parameters<typeof subscribeToConversationChanges>[0]
    )
    return () => {
      unsubscribe()
      for (const t of reconcileTimersRef.current) clearTimeout(t)
      reconcileTimersRef.current = []
    }
  }, [enabled])

  return counts
}
